import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { IFormCondition, IFormQuestion, IFormQuestionPresetOption } from '@fi-shared/fi-form-shared/fi-form-shared';
import { FormConditionType } from '@fi-shared/fi-form-shared/fi-form-enums';
import { MatSelectChange } from '@angular/material/select';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { isDefined } from '@fi-shared/utils';
import { FiFormConditionHelperService } from '@fi-shared/fi-form-shared/fi-form-condition-helper.service';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-form-logic-condition-if-is-predicate',
    templateUrl: './fi-form-logic-condition-if-is-predicate.component.html',
    styleUrls: ['./fi-form-logic-condition-if-is-predicate.component.scss'],
    providers: [{ provide: FiComponentsContainer, useExisting: FiFormLogicConditionIfIsPredicateComponent }],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-form-logic-condition-if-is-predicate' },
    standalone: false,
})
export class FiFormLogicConditionIfIsPredicateComponent extends FiComponentsContainer {
    @Input() condition: IFormCondition;
    @Input() formQuestions: IFormQuestion[] = [];
    @Input() selectedLanguageId: number;
    @Input() rootCondition: IFormCondition;
    @Input() parentCondition: IFormCondition;
    @Input() isChildCondition: boolean;
    formConditionType: typeof FormConditionType = FormConditionType;

    get selectedSourceQuestion(): IFormQuestion {
        return this.formQuestions.find((question: IFormQuestion) => question.uid === this.condition.sourceQuestionUid);
    }

    constructor(
        private formHelperService: FiFormHelperService,
        private formConditionHelperService: FiFormConditionHelperService,
        private appTextService: AppTextService,
    ) {
        super();
    }

    getIfSelectLabel(): string {
        if (!isDefined(this.condition.ConditionOperator)) return this.appTextService.get('form.condition_if');

        return (
            this.formConditionHelperService.getConditionOperatorText(this.condition) +
            ' ' +
            this.appTextService.get('form.condition_if').toLowerCase()
        );
    }

    isPresetOptionDisabled(option: IFormQuestionPresetOption): boolean {
        if (this.isOnlyCondition()) return false;

        const rootChildren: IFormCondition[] = this.formConditionHelperService.concatenateChildItems(
            this.rootCondition,
            false,
        );
        const currentQuestionConditions: IFormCondition[] = rootChildren.filter((childItem: IFormCondition) => {
            return childItem.sourceQuestionUid === this.selectedSourceQuestion.uid;
        });

        return currentQuestionConditions.length <= 1
            ? false
            : currentQuestionConditions.some((condition: IFormCondition) => {
                  return condition.presetOptionUid === option.uid;
              });
    }

    private isOnlyCondition(): boolean {
        return (
            !isDefined(this.selectedSourceQuestion) ||
            !isDefined(this.parentCondition) ||
            !isDefined(this.rootCondition)
        );
    }

    getQuestionName(question: IFormQuestion): string {
        return this.formHelperService.getQuestionName(question, this.selectedLanguageId, this.formQuestions);
    }

    getPresetOptionName(option: IFormQuestionPresetOption): string {
        return this.formHelperService.getPresetOptionName(option, this.selectedLanguageId, this.selectedSourceQuestion);
    }

    getIsConditionLabel(): string {
        return this.formHelperService.getIsConditionLabel(this.selectedSourceQuestion);
    }

    setSelectedSourceQuestion(event: MatSelectChange) {
        this.condition.ConditionType =
            this.isMultipleChoice() || this.isSingleChoice() ? this.formConditionType.EqualsToPresetOption : undefined;

        // clear selected preset options
        // https://chainformation.atlassian.net/browse/FI-4984
        this.condition.presetOptionUid = undefined;
        this.condition.ConditionPresetOptionIds = [];
        this.condition.presetOptionUids = [];
    }

    setConditionPresetOptionIndex(event: MatSelectChange) {
        if (this.condition.presetOptionUids.length) {
            this.condition.presetOptionUids = [];
        }
        this.condition.presetOptionUids.push(event.value);
    }

    isPresetOptionMissingValue(option: IFormQuestionPresetOption): boolean {
        if (this.selectedLanguageId) {
            const name: IContentLanguageLabelModel = option.Names.find((name: IContentLanguageLabelModel) => {
                return name.ContentLanguageId === this.selectedLanguageId;
            });
            return !name.Value.trim();
        }
    }

    isSingleChoice(): boolean {
        if (this.selectedSourceQuestion) {
            return this.formHelperService.isSingleChoiceQuestion(this.selectedSourceQuestion);
        }
    }

    isSimpleCondition(): boolean {
        if (this.selectedSourceQuestion) {
            return this.formHelperService.isSimpleCondition(this.selectedSourceQuestion);
        }
    }

    isMultipleChoice(): boolean {
        if (this.selectedSourceQuestion) {
            return this.formHelperService.isMultipleChoiceQuestion(this.selectedSourceQuestion);
        }
    }

    isSourceQuestionDisabled(question: IFormQuestion): boolean {
        return (
            this.isHeadingElement(question) ||
            this.isTextMessageElement(question) ||
            this.isMediaBankElement(question) ||
            this.isQuestionUsed(question) ||
            this.isTargetQuestion(question)
        );
    }

    private isQuestionUsed(question: IFormQuestion): boolean {
        if (this.formHelperService.isSimpleCondition(question)) {
            const conditions = this.formConditionHelperService.concatenateChildItems(this.rootCondition, false);
            return conditions.some((condition: IFormCondition) => condition.sourceQuestionUid === question.uid);
        }
        return false;
    }

    private isTargetQuestion(question: IFormQuestion): boolean {
        return this.rootCondition.Actions.some((conditionAction) => conditionAction.targetQuestionUid === question.uid);
    }

    isQuestionMissingValue(question: IFormQuestion): boolean {
        return this.formHelperService.isQuestionMissingValue(question, this.selectedLanguageId);
    }

    private isMediaBankElement(question: IFormQuestion): boolean {
        return this.formHelperService.isMediaBankElement(question);
    }

    private isTextMessageElement(question: IFormQuestion): boolean {
        return this.formHelperService.isTextMessageElement(question);
    }

    private isHeadingElement(question: IFormQuestion): boolean {
        return this.formHelperService.isHeadingElement(question);
    }
}
